/**
 * DashboardHomeV2 — HOME (Centrum dowodzenia) — MASTER v1.0
 *
 * Home = EKRAN PRACY użytkownika. NIE jest marketingiem, NIE jest Start Mode,
 * NIE jest Success Screen. Pomaga prowadzić hodowlę lub firmę i odpowiada na 3 pytania:
 *   • Co mam?    → hodowla/firma, strony, reproduktory, mioty, usługi
 *   • Co działa? → opublikowane strony, widoczność w katalogu, zapytania
 *   • Co dalej?  → następne rekomendowane kroki (zależne od stanu)
 *
 * Treść i rekomendacje zmieniają się zależnie od stanu użytkownika (4 stany):
 *   Nowy hodowca / Aktywny hodowca / Nowa firma / Aktywna firma.
 *
 * Start Mode (wybór Hodowca/Firma) = OSOBNY ekran `/onboarding/profile-type`.
 * Brak workspace (brak profile_type i brak profilu) → przekierowanie na Start Mode.
 *
 * SZKIELET (chrome): identyczny jak w kreatorach — StudioShell + kontener
 * `max-w-[1600px] mx-auto px-3 sm:px-6` + układ „lewa treść (flex-1) | prawy panel ~420px".
 * Prawy panel „Twoja strona" odpowiada podglądowi z kreatorów → spójne wrażenie jednego workspace.
 * Paleta Navy / Orange. Zero fioletu, zero sztucznych KPI.
 */
import { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowUpRight, ArrowRight, Bell, Sparkles, Loader2, Globe2,
  Check, MessageCircle, Search, Megaphone, Eye,
  PawPrint, Dog, Baby, ClipboardList, Briefcase, Plus,
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/utils/api';
import PublishSuccessModal from '@/components/PublishSuccessModal';
import StudioShell from '@/components/studio/StudioShell';
import WelcomeOnboardingModal, { WELCOME_DISMISS_KEY } from '@/components/onboarding/WelcomeOnboardingModal';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/* Polska odmiana liczebników (1 / 2-4 / 5+) */
function pl(n, one, few, many) {
  if (n === 1) return one;
  const m10 = n % 10, m100 = n % 100;
  if (m10 >= 2 && m10 <= 4 && !(m100 >= 12 && m100 <= 14)) return few;
  return many;
}

/* ── Podgląd strony (miniatura) ── */
function MiniLandingPreview({ profile, isBiznes }) {
  const heroImage = profile?.cover_image || profile?.cover_photo || profile?.logo || (profile?.gallery && profile.gallery[0]) || (profile?.photos && profile.photos[0]) || null;
  const subtitle = isBiznes
    ? (profile?.category || profile?.city || 'Twoja firma')
    : ((profile?.breeds || []).slice(0, 2).join(', ') || profile?.city || 'Twoja hodowla');

  if (!profile || !heroImage) {
    return (
      <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[var(--polish-bg-soft)] border border-[var(--polish-border)] flex flex-col items-center justify-center p-5 text-center">
        <div className="w-11 h-11 rounded-xl bg-[var(--apisai-primary-soft)] border border-[var(--apisai-primary-ring)] flex items-center justify-center mb-2.5">
          <Globe2 className="w-5 h-5 text-[var(--apisai-primary)]" />
        </div>
        <p className="text-[var(--apisai-text-primary)] text-sm font-semibold">Podgląd Twojej strony</p>
        <p className="text-[var(--apisai-text-muted)] text-xs mt-1 max-w-[210px]">
          {isBiznes
            ? 'Dodaj pierwsze informacje, a APISAI zbuduje z nich publiczną stronę firmy.'
            : 'Dodaj pierwsze informacje, a APISAI zbuduje z nich publiczną stronę hodowli.'}
        </p>
      </div>
    );
  }
  return (
    <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-[var(--studio-bg-canvas)] border border-[var(--polish-border)] relative group shadow-[var(--polish-shadow-card)]" data-testid="mini-landing-preview">
      <img src={heroImage} alt={profile.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/15 to-black/80" />
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <p className="text-white text-base font-bold leading-tight truncate" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.6)' }}>{profile.name}</p>
        <p className="text-zinc-200 text-[11px] mt-0.5 italic line-clamp-1">{subtitle}</p>
      </div>
    </div>
  );
}

/* ── Nagłówek sekcji ── */
function SectionHead({ kicker, title, subtitle, testid }) {
  return (
    <div className="space-y-1" data-testid={testid}>
      <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--apisai-primary)]">{kicker}</p>
      <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-[var(--apisai-text-primary)]">{title}</h2>
      {subtitle && <p className="text-[var(--apisai-text-secondary)] text-sm">{subtitle}</p>}
    </div>
  );
}

/* ── Karta „Co mam" (inwentarz) ── */
function HaveCard({ icon: Icon, label, value, hint, cta, onClick, statusTone, testid }) {
  const toneRing = statusTone === 'ok' ? 'ring-1 ring-[var(--apisai-success)]/30'
    : statusTone === 'warn' ? 'ring-1 ring-[var(--apisai-primary-ring)]' : '';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`apisai-soft-card text-left p-4 sm:p-5 flex flex-col gap-2.5 group hover:shadow-[var(--polish-shadow-card)] hover:-translate-y-0.5 transition-all ${toneRing}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-9 h-9 rounded-lg bg-[var(--polish-bg-soft)] flex items-center justify-center flex-shrink-0">
          <Icon className="w-4.5 h-4.5 text-[var(--apisai-text-secondary)]" strokeWidth={1.9} />
        </div>
        <p className="text-[var(--apisai-text-muted)] text-[11px] font-semibold uppercase tracking-wider">{label}</p>
      </div>
      <div className="min-w-0">
        <p className="text-[var(--apisai-text-primary)] text-lg font-bold leading-tight truncate">{value}</p>
        {hint && <p className="text-[var(--apisai-text-muted)] text-xs mt-0.5 leading-snug">{hint}</p>}
      </div>
      <span className="mt-auto pt-1 text-[var(--apisai-primary)] text-xs font-semibold inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
        {cta} <ArrowUpRight className="w-3.5 h-3.5" />
      </span>
    </button>
  );
}

/* ── Kafelek „Co działa" ── */
function LiveTile({ icon: Icon, label, value, hint, cta, onClick, highlight, badge, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`apisai-soft-card text-left p-5 flex flex-col gap-3 group hover:shadow-[var(--polish-shadow-card)] transition-all ${highlight ? 'ring-1 ring-[var(--apisai-primary-ring)]' : ''}`}
    >
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-[var(--polish-bg-soft)] flex items-center justify-center">
          <Icon className="w-4 h-4 text-[var(--apisai-text-secondary)]" />
        </div>
        <p className="text-[var(--apisai-text-secondary)] text-xs font-medium uppercase tracking-wider">{label}</p>
        {badge != null && badge > 0 && (
          <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--apisai-primary)] text-white text-[10px] font-bold flex items-center justify-center">{badge}</span>
        )}
      </div>
      <div>
        <p className="text-[var(--apisai-text-primary)] text-lg font-semibold leading-tight">{value}</p>
        {hint && <p className="text-[var(--apisai-text-muted)] text-xs mt-1">{hint}</p>}
      </div>
      <span className="text-[var(--apisai-primary)] text-xs font-semibold inline-flex items-center gap-1 group-hover:gap-1.5 transition-all">
        {cta} <ArrowUpRight className="w-3 h-3" />
      </span>
    </button>
  );
}

/* ── Pozycja „Co dalej" (rekomendacja) ── */
function NextStep({ icon: Icon, title, sub, ctaLabel, onClick, primary, urgent, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={[
        'group w-full text-left flex items-center gap-4 p-4 sm:p-5 rounded-2xl border transition-all hover:-translate-y-0.5 hover:shadow-[var(--polish-shadow-card)]',
        primary
          ? 'bg-[var(--apisai-primary)]/[0.06] border-[var(--apisai-primary-ring)]'
          : urgent
            ? 'bg-[var(--apisai-primary)]/[0.04] border-[var(--apisai-primary-ring)]'
            : 'bg-white border-[var(--polish-border)] hover:border-[var(--apisai-primary-ring)]',
      ].join(' ')}
    >
      <div
        className="flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center"
        style={{ background: primary ? 'var(--apisai-primary)' : 'rgba(249,115,22,0.10)' }}
      >
        <Icon className="w-5 h-5" style={{ color: primary ? '#fff' : 'var(--apisai-primary)' }} strokeWidth={1.9} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[var(--apisai-text-primary)] text-base font-bold leading-tight">{title}</p>
        {sub && <p className="text-[var(--apisai-text-secondary)] text-sm mt-0.5 leading-snug">{sub}</p>}
      </div>
      <span className="flex-shrink-0 inline-flex items-center gap-1.5 text-[var(--apisai-primary)] text-sm font-semibold">
        <span className="hidden sm:inline">{ctaLabel}</span>
        <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
      </span>
    </button>
  );
}

/* ── Prawy panel „Twoja strona" (mirror podglądu z kreatorów) ── */
function StronaPanel({ isBiznes, profile, hasProfile, publicUrl, stronaStatus, navigate }) {
  return (
    <div className="apisai-soft-card p-5 flex flex-col gap-4" data-testid="home-have-strona">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[var(--apisai-text-muted)] text-[11px] font-semibold uppercase tracking-wider">
            {isBiznes ? 'Strona firmy' : 'Strona hodowli'}
          </p>
          <p className="text-[var(--apisai-text-primary)] text-lg font-bold leading-tight mt-1 truncate">
            {profile?.name || 'Jeszcze nie utworzona'}
          </p>
        </div>
        <span className={[
          'flex-shrink-0 text-[11px] font-bold px-2.5 py-1 rounded-full',
          stronaStatus.tone === 'ok' ? 'bg-[var(--apisai-success-soft)] text-[var(--apisai-success)]'
            : stronaStatus.tone === 'warn' ? 'bg-[var(--apisai-primary-soft)] text-[var(--apisai-primary-700)]'
            : 'bg-[var(--polish-bg-soft)] text-[var(--apisai-text-muted)]',
        ].join(' ')}>
          {stronaStatus.label}
        </span>
      </div>

      <MiniLandingPreview profile={profile} isBiznes={isBiznes} />

      <div className="flex flex-wrap gap-2 mt-auto">
        {hasProfile ? (
          <button
            onClick={() => navigate(isBiznes ? (profile?.id ? `/wizard/firma-v2/${profile.id}` : '/wizard/firma-v2') : (profile?.id ? `/wizard/hodowla-v2/${profile.id}` : '/wizard/hodowla-v2'))}
            data-testid="home-strona-edit-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-sm font-semibold transition-colors shadow-[var(--shadow-primary)]"
          >
            Edytuj stronę <ArrowUpRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            onClick={() => navigate(isBiznes ? '/wizard/firma-v2' : '/wizard/hodowla-v2')}
            data-testid="home-strona-create-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-sm font-semibold transition-colors shadow-[var(--shadow-primary)]"
          >
            {isBiznes ? 'Zacznij tworzyć stronę firmy' : 'Zacznij tworzyć stronę'} <ArrowRight className="w-4 h-4" />
          </button>
        )}
        {publicUrl && (
          <a
            href={publicUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="home-strona-view-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[var(--polish-surface)] hover:bg-[var(--polish-surface-soft)] border border-[var(--polish-border)] text-[var(--apisai-text-secondary)] text-sm font-medium transition-colors"
          >
            <Eye className="w-4 h-4" /> Podgląd publiczny
          </a>
        )}
      </div>

      <p className="text-[var(--apisai-text-muted)] text-[11px] leading-snug" data-testid="home-strona-trust">
        Ty decydujesz, co publikujesz. Dane możesz edytować lub ukryć w każdej chwili.
      </p>
    </div>
  );
}

/* ── Silnik rekomendacji „Co dalej" — wyłącznie na bazie realnych danych ── */
function buildNextSteps({ isBiznes, hasProfile, isPublished, counts, newLeads, hasPosts, navigate }) {
  const steps = [];

  if (!hasProfile) {
    steps.push({
      key: 'create', primary: true, icon: Globe2,
      title: isBiznes ? 'Utwórz stronę firmy' : 'Utwórz stronę hodowli',
      sub: 'To podstawa — zajmie tylko kilka minut.',
      ctaLabel: 'Zacznij', onClick: () => navigate(isBiznes ? '/wizard/firma-v2' : '/wizard/hodowla-v2'),
    });
    return steps; // nowy user — jeden wyraźny fokus
  }

  if (!isPublished) {
    steps.push({
      key: 'publish', primary: true, icon: Globe2,
      title: isBiznes ? 'Dokończ i opublikuj stronę firmy' : 'Dokończ i opublikuj stronę hodowli',
      sub: 'Strona jest w wersji roboczej — opublikuj, by trafić do katalogu APISAI.',
      ctaLabel: 'Dokończ', onClick: () => navigate(isBiznes ? '/wizard/firma-v2' : '/wizard/hodowla-v2'),
    });
  }

  if (newLeads > 0) {
    steps.push({
      key: 'leads', urgent: true, icon: MessageCircle,
      title: `Odpowiedz na ${newLeads} ${pl(newLeads, 'nowe zapytanie', 'nowe zapytania', 'nowych zapytań')}`,
      sub: 'Klienci czekają — szybka odpowiedź zwiększa szansę na sprzedaż.',
      ctaLabel: 'Otwórz', onClick: () => navigate('/panel/leady'),
    });
  }

  if (!isBiznes) {
    if (counts.suki === 0) steps.push({ key: 'suka', icon: Dog, title: 'Dodaj pierwszą sukę', sub: 'Pokaż swoje hodowlane suki.', ctaLabel: 'Dodaj', onClick: () => navigate('/panel/psy') });
    if (counts.reproduktory === 0) steps.push({ key: 'repro', icon: PawPrint, title: 'Dodaj reproduktora', sub: 'Reproduktory budują prestiż hodowli.', ctaLabel: 'Dodaj', onClick: () => navigate('/panel/reproduktory') });
    if (counts.mioty === 0) steps.push({ key: 'miot', icon: Baby, title: 'Dodaj miot', sub: 'Mioty przyciągają najwięcej zapytań od klientów.', ctaLabel: 'Dodaj', onClick: () => navigate('/panel/mioty') });
  } else if (counts.uslugi === 0) {
    steps.push({ key: 'oferta', icon: ClipboardList, title: 'Dodaj ofertę usług', sub: 'Pokaż klientom dokładnie, co oferujesz.', ctaLabel: 'Dodaj', onClick: () => navigate('/panel/strona-firmy?krok=oferta') });
  }

  return steps;
}

/* ── Pasek postępu konta (Hodowla / Biznes) — prowadzi do kompletnego profilu ── */
function AccountProgress({ steps, doneCount, requiredCount, pct, isBiznes, isPublished }) {
  const total = requiredCount ?? steps.length;
  const complete = pct === 100;
  // Komunikat dopasowany do stanu: przed publikacją prowadzimy do publikacji,
  // po publikacji — do „ożywienia" strony psami/ofertą (bez psów strona jest martwa).
  const headline = complete
    ? (isBiznes ? 'Profil firmy kompletny 🎉' : 'Profil hodowli kompletny 🎉')
    : !isPublished
      ? `Dokończ ${total} kroki, żeby opublikować stronę`
      : (isBiznes
          ? 'Uzupełnij ofertę, żeby strona przyciągała klientów'
          : 'Dodaj psy, żeby Twoja strona ożyła');
  return (
    <section className="apisai-soft-card p-5 sm:p-6 mb-8" data-testid="account-progress">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[var(--apisai-primary)]">Postęp konta</p>
          <h2 className="text-lg sm:text-xl font-bold text-[var(--apisai-text-primary)] mt-0.5 leading-tight">
            {headline}
          </h2>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-2xl font-bold text-[var(--apisai-text-primary)] leading-none">{pct}%</p>
          <p className="text-[11px] text-[var(--apisai-text-muted)] mt-1">{doneCount}/{total} kroków</p>
        </div>
      </div>
      <div className="h-2 rounded-full bg-[var(--polish-bg-soft)] overflow-hidden mb-4">
        <div className="h-full rounded-full bg-[var(--apisai-primary)] transition-all duration-700" style={{ width: `${pct}%` }} />
      </div>
      <div className={`grid gap-2.5 ${steps.length >= 5 ? 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5' : steps.length === 3 ? 'grid-cols-1 sm:grid-cols-3' : 'grid-cols-2 sm:grid-cols-4'}`}>
        {steps.map((s) => (
          <button
            key={s.key}
            type="button"
            onClick={s.onClick}
            data-testid={`progress-step-${s.key}`}
            aria-label={`${s.label} — ${s.done ? 'gotowe' : (s.optional ? 'opcjonalne' : 'do zrobienia')}`}
            className={`flex items-center gap-2.5 p-2.5 rounded-xl border text-left transition-all hover:-translate-y-0.5 ${
              s.done
                ? 'border-[var(--apisai-success)]/30 bg-[var(--apisai-success-soft)]'
                : s.optional
                  ? 'border-[var(--polish-border)] bg-[var(--polish-bg-soft)]/60 hover:bg-[var(--polish-bg-soft)]'
                  : 'border-[var(--apisai-primary-ring)] bg-[var(--apisai-primary)]/[0.04] hover:bg-[var(--apisai-primary)]/[0.08]'
            }`}
          >
            <span className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${
              s.done
                ? 'bg-[var(--apisai-success)] text-white'
                : s.optional
                  ? 'bg-white border border-[var(--polish-border)] text-[var(--apisai-text-muted)]'
                  : 'bg-white border border-[var(--apisai-primary-ring)] text-[var(--apisai-primary)]'
            }`}>
              {s.done ? <Check className="w-4 h-4" strokeWidth={3} /> : <s.icon className="w-4 h-4" />}
            </span>
            <span className="min-w-0">
              <span className="flex items-center gap-1.5 text-[13px] font-semibold text-[var(--apisai-text-primary)]">
                <span className="truncate">{s.label}</span>
                {s.optional && !s.done && (
                  <span className="flex-shrink-0 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[var(--polish-bg-soft)] text-[var(--apisai-text-muted)] border border-[var(--polish-border)]">opcja</span>
                )}
              </span>
              <span className={`block text-[11px] font-medium ${s.done ? 'text-[var(--apisai-success)]' : s.optional ? 'text-[var(--apisai-text-muted)]' : 'text-[var(--apisai-primary)]'}`}>
                {s.done ? 'Gotowe' : (s.desc || 'Do zrobienia')}
              </span>
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

/* ═════════════════════ MAIN ═════════════════════ */
export default function DashboardHomeV2() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [counts, setCounts] = useState({ suki: 0, reproduktory: 0, mioty: 0, uslugi: 0 });
  const [leads, setLeads] = useState([]);
  const [hasPosts, setHasPosts] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [profileChecked, setProfileChecked] = useState(false);

  const isBiznes = user?.profile_type === 'biznes';

  // Ekran powitalny: tylko gdy zapytanie o profil SIĘ POWIODŁO i potwierdziło brak strony.
  // (Wcześniej modal pokazywał się też przy błędzie sieci/401 u stałych użytkowników.)
  useEffect(() => {
    if (loading || !user?.id || profile || !profileChecked) return;
    let dismissed = false;
    try { dismissed = localStorage.getItem(WELCOME_DISMISS_KEY) === '1'; } catch (e) { /* noop */ }
    if (!dismissed) setShowWelcome(true);
  }, [loading, user?.id, profile, profileChecked]);

  const dismissWelcome = () => {
    try { localStorage.setItem(WELCOME_DISMISS_KEY, '1'); } catch (e) { /* noop */ }
    setShowWelcome(false);
  };

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const profileUrl = isBiznes ? `${API}/business/my` : `${API}/kennels`;
        const [pRes, lRes] = await Promise.allSettled([
          axios.get(profileUrl, { headers: headers() }),
          axios.get(`${API}/panel/leads/all`, { headers: headers() }),
        ]);

        const pData = pRes.status === 'fulfilled' ? pRes.value.data : null;
        const items = Array.isArray(pData?.items) ? pData.items
          : Array.isArray(pData) ? pData
          : (pData?.id || pData?.slug) ? [pData] : [];
        const myProfile = items.length > 0 ? items[0] : null;

        const lData = lRes.status === 'fulfilled' ? lRes.value.data : null;
        const leadsList = Array.isArray(lData?.items) ? lData.items : Array.isArray(lData) ? lData : [];

        let nextCounts = { suki: 0, reproduktory: 0, mioty: 0, uslugi: 0 };
        let postsFlag = false;

        if (myProfile?.id) {
          if (!isBiznes) {
            const [dRes, litRes] = await Promise.allSettled([
              axios.get(`${API}/kennels/${myProfile.id}/dogs`, { headers: headers() }),
              axios.get(`${API}/litters`, { headers: headers() }),
            ]);
            const dogs = dRes.status === 'fulfilled' && Array.isArray(dRes.value.data) ? dRes.value.data : [];
            nextCounts.suki = dogs.filter((d) => d.gender === 'female' && d.listing_type !== 'stud' && !d.is_stud).length;
            nextCounts.reproduktory = dogs.filter((d) => d.gender === 'male').length;
            const lits = litRes.status === 'fulfilled' && Array.isArray(litRes.value.data) ? litRes.value.data : [];
            nextCounts.mioty = lits.length;
          } else {
            nextCounts.uslugi = Array.isArray(myProfile.services) ? myProfile.services.length
              : Array.isArray(myProfile.offerings) ? myProfile.offerings.length : 0;
          }
        }

        if (!cancelled) {
          setProfile(myProfile);
          setProfileChecked(pRes.status === 'fulfilled');
          setLeads(leadsList);
          setCounts(nextCounts);
          setHasPosts(postsFlag);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, isBiznes]);

  // ── Stany ładowania / przekierowania ──
  if (authLoading || (loading && user?.id)) {
    return (
      <StudioShell mode="workspace" activeKey="home" contextLabel="Centrum dowodzenia">
        <div className="min-h-[60vh] flex items-center justify-center" data-testid="dashboard-loading">
          <Loader2 className="w-8 h-8 text-[var(--apisai-primary)] animate-spin" />
        </div>
      </StudioShell>
    );
  }

  // Brak workspace (brak typu profilu I brak profilu) → Start Mode = osobny ekran
  if (!user?.profile_type && !profile) {
    return <Navigate to="/onboarding/profile-type" replace />;
  }

  const greetingName = user?.name?.split(' ')[0] || 'Witaj';
  const hasProfile = !!profile;
  const isPublished = !!profile?.slug && profile?.status !== 'draft';
  const publicUrl = profile?.slug ? `/${isBiznes ? 'firma' : 'hodowla'}/${profile.slug}` : null;
  const newLeads = leads.filter((l) => (l?.status || 'new') === 'new').length;

  const stateKey = isBiznes
    ? (isPublished ? 'aktywna-firma' : 'nowa-firma')
    : (isPublished ? 'aktywny-hodowca' : 'nowy-hodowca');

  const nextSteps = buildNextSteps({ isBiznes, hasProfile, isPublished, counts, newLeads, hasPosts, navigate }).slice(0, 4);

  const stronaStatus = isPublished
    ? { label: 'Opublikowana', tone: 'ok' }
    : hasProfile
      ? { label: 'Wersja robocza', tone: 'warn' }
      : { label: 'Nie utworzona', tone: null };

  // ── Pasek postępu konta — kroki adaptują się do typu profilu (Hodowla / Biznes) ──
  const profileEditPath = isBiznes
    ? (profile?.id ? `/wizard/firma-v2/${profile.id}` : '/wizard/firma-v2')
    : (profile?.id ? `/wizard/hodowla-v2/${profile.id}` : '/wizard/hodowla-v2');
  const progressSteps = isBiznes
    ? [
        { key: 'profil', label: 'Profil firmy', desc: 'Dodaj opis i kontakt', icon: Briefcase, done: hasProfile, onClick: () => navigate(profileEditPath) },
        { key: 'publikacja', label: 'Publikacja', desc: 'Pokaż stronę w katalogu', icon: Globe2, done: isPublished, onClick: () => navigate(profileEditPath) },
        { key: 'oferta', label: 'Oferta usług', desc: 'Pokaż, co oferujesz', icon: ClipboardList, done: counts.uslugi > 0, onClick: () => navigate('/panel/strona-firmy?krok=oferta') },
      ]
    : [
        { key: 'profil', label: 'Strona hodowli', desc: 'Dodaj opis i kontakt', icon: Globe2, done: hasProfile, onClick: () => navigate(profileEditPath) },
        { key: 'publikacja', label: 'Publikacja', desc: 'Pokaż stronę w katalogu', icon: Sparkles, done: isPublished, onClick: () => navigate(profileEditPath) },
        { key: 'suki', label: 'Dodaj suki', desc: 'Podstawa Twojej hodowli', icon: Dog, done: counts.suki > 0, onClick: () => navigate('/panel/psy') },
        { key: 'miot', label: 'Pierwszy miot', desc: 'Oferta szczeniąt', icon: Baby, done: counts.mioty > 0, onClick: () => navigate('/panel/mioty') },
        { key: 'reproduktor', label: 'Reproduktor', desc: 'Nie każdy ma', icon: PawPrint, done: counts.reproduktory > 0, optional: true, onClick: () => navigate('/panel/reproduktory') },
      ];
  // Reproduktor jest opcjonalny — nie liczy się do wymaganego postępu (nie każda hodowla go ma).
  const requiredSteps = progressSteps.filter((s) => !s.optional);
  const progressDone = requiredSteps.filter((s) => s.done).length;
  const progressPct = Math.round((progressDone / requiredSteps.length) * 100);

  return (
    <StudioShell mode="workspace" activeKey="home" contextLabel="Centrum dowodzenia">
      <PublishSuccessModal />
      {showWelcome && <WelcomeOnboardingModal onClose={dismissWelcome} />}
      <main className="flex-1 overflow-y-auto" data-testid="dashboard-home" data-home-state={stateKey}>
        {/* Nagłówek */}
        <header className="flex items-center justify-between px-4 sm:px-6 py-4 border-b border-[var(--polish-border)]">
          <div className="flex items-center gap-2 text-sm min-w-0">
            <span className="text-[var(--apisai-text-muted)] hidden sm:inline">Dzień dobry,</span>
            <span className="text-[var(--apisai-text-primary)] font-semibold truncate">{greetingName}</span>
            <Sparkles className="w-4 h-4 text-[var(--apisai-accent-amber)] flex-shrink-0" strokeWidth={2.5} />
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            {publicUrl && (
              <a
                href={publicUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="preview-page-btn"
                className="hidden sm:flex items-center gap-1.5 px-3 py-2 rounded-xl bg-[var(--polish-surface)] hover:bg-[var(--polish-surface-soft)] border border-[var(--polish-border)] text-[var(--apisai-text-secondary)] text-xs font-medium transition-colors"
              >
                Podgląd mojej strony <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            )}
            <button className="relative p-2 rounded-xl bg-[var(--polish-surface)] hover:bg-[var(--polish-surface-soft)] border border-[var(--polish-border)] transition-colors" data-testid="notifications-btn">
              <Bell className="w-4 h-4 text-[var(--apisai-text-muted)]" />
              {newLeads > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[var(--apisai-primary)] text-[9px] font-bold text-white flex items-center justify-center">{newLeads}</span>
              )}
            </button>
          </div>
        </header>

        {/* Kontener identyczny jak w kreatorach: max-w-[1600px] + px-3/px-6 (treść startuje przy menu) */}
        <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 lg:py-8 pb-24 lg:pb-8">
          {/* Powitanie + kontekst stanu (pełna szerokość) */}
          <section className="space-y-2 mb-8" data-testid="home-greeting">
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[var(--apisai-text-primary)] leading-[1.1]">
              {hasProfile ? (profile?.name || (isBiznes ? 'Twoja firma' : 'Twoja hodowla')) : `Cześć, ${greetingName}!`}
            </h1>
            {hasProfile ? (
              <p className="text-[var(--apisai-text-secondary)] text-base">
                {isPublished
                  ? (isBiznes ? 'Twoja firma jest online. Oto co masz, co działa i co warto zrobić dalej.' : 'Twoja hodowla jest online. Oto co masz, co działa i co warto zrobić dalej.')
                  : 'Profil jest w toku. Dokończ i opublikuj stronę, żeby pojawić się w katalogu APISAI.'}
              </p>
            ) : (
              <div className="space-y-4 pt-1" data-testid="home-hero-cta-block">
                <div className="space-y-1.5">
                  <p className="text-[var(--apisai-text-primary)] text-lg sm:text-xl font-semibold">
                    {isBiznes ? 'Zacznij od stworzenia strony firmy' : 'Zacznij od stworzenia strony hodowli'}
                  </p>
                  <p className="text-[var(--apisai-text-secondary)] text-base max-w-xl">
                    Dodaj opis, zdjęcia i dane kontaktowe. Potem opublikujesz stronę w katalogu APISAI.
                  </p>
                </div>
                <div className="flex flex-col items-start gap-1.5">
                  <button
                    onClick={() => navigate(isBiznes ? '/wizard/firma-v2' : '/wizard/hodowla-v2')}
                    data-testid="home-hero-create-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[var(--apisai-primary)] hover:bg-[var(--apisai-primary-600)] text-white text-sm sm:text-base font-semibold transition-colors shadow-[var(--shadow-primary)]"
                  >
                    {isBiznes ? 'Utwórz stronę firmy' : 'Utwórz stronę hodowli'} <ArrowRight className="w-4 h-4" />
                  </button>
                  <p className="text-[var(--apisai-text-muted)] text-xs">Zajmie około 5 minut. Możesz wrócić i dokończyć później.</p>
                </div>
              </div>
            )}
          </section>

          {/* Pasek postępu konta — prowadzi do kompletnego profilu (Hodowla / Biznes) */}
          <AccountProgress steps={progressSteps} doneCount={progressDone} requiredCount={requiredSteps.length} pct={progressPct} isBiznes={isBiznes} isPublished={isPublished} />

          {/* WORKSPACE: lewa kolumna treści (flex-1) | prawy panel „Twoja strona" (~420px) — szkielet jak w kreatorach */}
          <div className="flex flex-col lg:flex-row gap-6 xl:gap-8 items-start">
            {/* ── LEWA KOLUMNA ── */}
            <div className="flex-1 min-w-0 w-full space-y-10 lg:space-y-12 order-2 lg:order-1">

              {/* ───────── CO MAM? ───────── */}
              <section className="space-y-5">
                <SectionHead kicker={hasProfile ? 'Co mam?' : 'Co możesz dodać?'} title={isBiznes ? 'Twoja firma w APISAI' : 'Twoja hodowla w APISAI'} testid="home-co-mam" />
                <div className={`grid gap-4 ${isBiznes ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-2'} content-start`}>
                  {isBiznes ? (
                    <>
                      <HaveCard icon={ClipboardList} label="Oferta usług" value={`${counts.uslugi} ${pl(counts.uslugi, 'usługa', 'usługi', 'usług')}`} hint={counts.uslugi === 0 ? 'Dodaj swoją pierwszą usługę' : 'Zarządzaj ofertą'} cta={counts.uslugi === 0 ? 'Dodaj' : 'Zarządzaj'} onClick={() => navigate('/panel/strona-firmy?krok=oferta')} testid="home-have-oferta" />
                      <HaveCard icon={Briefcase} label="Profil firmy" value={hasProfile ? 'Utworzony' : 'Brak'} hint="Dane, kontakt, logo" cta="Edytuj" onClick={() => navigate(profile?.id ? `/wizard/firma-v2/${profile.id}` : '/wizard/firma-v2')} testid="home-have-profil-firma" />
                    </>
                  ) : (
                    <>
                      <HaveCard icon={Dog} label="Suki" value={`${counts.suki}`} hint={counts.suki === 0 ? 'Dodaj pierwszą sukę' : pl(counts.suki, 'suka w hodowli', 'suki w hodowli', 'suk w hodowli')} cta={counts.suki === 0 ? 'Dodaj' : 'Zarządzaj'} onClick={() => navigate('/panel/psy')} testid="home-have-suki" />
                      <HaveCard icon={PawPrint} label="Reproduktory" value={`${counts.reproduktory}`} hint={counts.reproduktory === 0 ? 'Dodaj reproduktora' : pl(counts.reproduktory, 'reproduktor', 'reproduktory', 'reproduktorów')} cta={counts.reproduktory === 0 ? 'Dodaj' : 'Zarządzaj'} onClick={() => navigate('/panel/reproduktory')} testid="home-have-reproduktory" />
                      <HaveCard icon={Baby} label="Mioty" value={`${counts.mioty}`} hint={counts.mioty === 0 ? 'Dodaj miot' : pl(counts.mioty, 'miot', 'mioty', 'miotów')} cta={counts.mioty === 0 ? 'Dodaj' : 'Zarządzaj'} onClick={() => navigate('/panel/mioty')} testid="home-have-mioty" />
                      <HaveCard icon={Sparkles} label="Usługi" value={'Krycia, opieka…'} hint="Dodatkowe usługi hodowli" cta="Zobacz" onClick={() => navigate('/panel/strona-hodowli')} testid="home-have-uslugi" />
                    </>
                  )}
                </div>
              </section>

              {/* ───────── CO DZIAŁA? ───────── */}
              <section className="space-y-5">
                <SectionHead kicker="Co działa?" title="Widoczność i zapytania" testid="home-co-dziala" />
                {isPublished ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                    <LiveTile
                      icon={Globe2}
                      label="Strona online"
                      value="Aktywna i widoczna"
                      hint="Klienci mogą ją otworzyć"
                      cta="Zobacz stronę"
                      onClick={() => window.open(publicUrl, '_blank')}
                      testid="home-live-strona"
                    />
                    <LiveTile
                      icon={Search}
                      label="Katalog APISAI"
                      value="W katalogu"
                      hint="Pojawiasz się w wyszukiwarce APISAI"
                      cta="Zobacz wpis"
                      onClick={() => window.open(isBiznes ? '/katalog/uslugi' : '/katalog', '_blank')}
                      testid="home-live-katalog"
                    />
                    <LiveTile
                      icon={MessageCircle}
                      label="Zapytania"
                      value={newLeads > 0 ? `${newLeads} ${pl(newLeads, 'nowe', 'nowe', 'nowych')}` : leads.length > 0 ? `${leads.length} łącznie` : 'Brak zapytań'}
                      hint={newLeads > 0 ? 'Klienci czekają na odpowiedź' : 'Tu trafiają wiadomości od klientów'}
                      cta="Otwórz skrzynkę"
                      onClick={() => navigate('/panel/leady')}
                      highlight={newLeads > 0}
                      badge={newLeads}
                      testid="home-live-zapytania"
                    />
                  </div>
                ) : (
                  <div className="apisai-soft-card p-5 flex items-start gap-4" data-testid="home-live-empty">
                    <div className="w-10 h-10 rounded-xl bg-[var(--polish-bg-soft)] flex items-center justify-center flex-shrink-0">
                      <Globe2 className="w-5 h-5 text-[var(--apisai-text-muted)]" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[var(--apisai-text-primary)] text-base font-semibold">Status widoczności: Nieopublikowane</p>
                      <p className="text-[var(--apisai-text-secondary)] text-sm mt-0.5">
                        {hasProfile
                          ? 'Opublikuj stronę, aby pojawić się w katalogu APISAI i móc zbierać zapytania.'
                          : 'Utwórz i opublikuj stronę, aby pojawić się w katalogu APISAI i móc zbierać zapytania.'}
                      </p>
                    </div>
                  </div>
                )}
              </section>

              {/* ───────── CO DALEJ? ───────── */}
              <section className="space-y-5">
                <SectionHead kicker="Co dalej?" title="Rekomendowane kroki" subtitle="Dobrane na podstawie tego, co już masz." testid="home-co-dalej" />
                <div className="space-y-3">
                  {nextSteps.length > 0 ? (
                    nextSteps.map((s) => (
                      <NextStep
                        key={s.key}
                        icon={s.icon}
                        title={s.title}
                        sub={s.sub}
                        ctaLabel={s.ctaLabel}
                        onClick={s.onClick}
                        primary={s.primary}
                        urgent={s.urgent}
                        testid={`home-next-${s.key}`}
                      />
                    ))
                  ) : (
                    <div className="apisai-soft-card p-5 flex items-center gap-3" data-testid="home-next-empty">
                      <Check className="w-5 h-5 text-[var(--apisai-success)]" strokeWidth={2.5} />
                      <p className="text-[var(--apisai-text-secondary)] text-sm">Świetna robota — masz wszystko uzupełnione. Wróć tu, gdy pojawią się nowe zapytania.</p>
                    </div>
                  )}
                </div>
              </section>
            </div>

            {/* ── PRAWY PANEL: „Twoja strona" (mirror podglądu z kreatorów) ── */}
            <aside
              className="w-full lg:w-[400px] xl:w-[440px] flex-shrink-0 order-1 lg:order-2 lg:sticky lg:top-6"
              data-testid="home-strona-rail"
            >
              <StronaPanel
                isBiznes={isBiznes}
                profile={profile}
                hasProfile={hasProfile}
                publicUrl={publicUrl}
                stronaStatus={stronaStatus}
                navigate={navigate}
              />

              {/* Pomoc — asystent APISAI (prawa kolumna) */}
              <div className="apisai-soft-card p-5 mt-4" data-testid="home-rail-help">
                <p className="text-[var(--apisai-text-primary)] text-sm font-bold">Potrzebujesz pomocy?</p>
                <p className="text-[var(--apisai-text-secondary)] text-[13px] mt-1">Asystent ApisAI odpowie na każde pytanie o platformę.</p>
                <button onClick={() => navigate('/panel/pomoc')} className="mt-3 inline-flex items-center gap-1 text-[13px] font-semibold text-[var(--apisai-primary)] hover:underline" data-testid="home-rail-help-open">
                  Otwórz pomoc <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </StudioShell>
  );
}
